<template>
  <div
    v-if="computedLink.isSamePageAndHasHash"
    class="util-route-link"
    @click="scrollToHash"
    @keydown.enter="scrollToHash"
  >
    <slot />
  </div>
  <NuxtLink
    v-else-if="computedLink.isInternal"
    v-bind="computedLink"
    class="util-route-link"
  >
    <slot />
  </NuxtLink>
  <a
    v-else
    v-bind="computedLink"
    class="util-route-link"
  >
    <slot />
  </a>
</template>

<script setup>
const props = defineProps({
    link: {
        type: [String, Object],
        default: '',
    },

    target: {
        type: String,
        default: '_self',
        validator: (value) => ['_blank', '_self'].includes(value),
    },

    filterQueryProp: {
        type: Array,
        default: () => [],
    },
});

const filterQuery = inject('filterQuery', isArrayNotEmpty(props.filterQueryProp) ? props.filterQueryProp[0] : null);

const localePath = useLocalePath();
const { locale } = useI18n();
const route = useRoute();

const checkIsExternal = (string) => string.includes('http://')
    || string.includes('https://')
    || string.includes('mailto:')
    || string.includes('tel:');

const computedLink = computed(() => {
    if (typeof props.link === 'object') {
        const filterQueryString = filterQuery ? buildUrlQueryString(filterQuery) : null;
        const link = usePrepareStoryblokLink(props.link, locale.value);
        const linkWithoutHash = link.split('#')[0];

        const hash = link.split('#')[1];
        const isInternal = !checkIsExternal(link);
        const isSamePageAndHasHash = route.path === linkWithoutHash && hash;

        return {
            isInternal,
            isSamePageAndHasHash,
            hash,
            ...(isInternal ? {
                to: filterQueryString ? `${link}?${filterQueryString}` : link,
            } : {
                href: link,
            }),
            target: isInternal ? props.target : '_blank',
            rel: props.target === '_blank' ? 'noopener noreferrer' : null,
        };
    }

    // Add mailto: to email links
    if (props.link.includes('@')) {
        return {
            isInternal: false,
            href: props.link,
            target: '_blank',
            rel: 'noopener noreferrer',
        };
    }

    const filterQueryString = filterQuery ? buildUrlQueryString(filterQuery) : null;

    const href = filterQueryString ? `${localePath(props.link)}?${filterQueryString}` : localePath(props.link);
    const isInternal = !checkIsExternal(props.link);

    return {
        isInternal,
        ...(isInternal ? {
            to: href,
        } : {
            href,
        }),
        target: props.target,
        rel: props.target === '_blank' ? 'noopener noreferrer' : null,
    };
});

const { lenis } = useLenis();
const { easeInOutQuart } = useEasing();

const scrollToHash = () => {
    lenis.value?.scrollTo(`#${slugify(computedLink.value.hash)}`, {
        offset: 100,
        duration: 1,
        lock: true,
        force: true,
        easing: easeInOutQuart,
    });
};
</script>

<style lang="scss">
.util-route-link {
    cursor: pointer;
}
</style>
